import React, { useState } from 'react'
import api from '../../../helpers/apiRequester'
import styles from './styles.module.scss'
import {
    BASE_URL,
    DeliveryRow,
    EXPORT_FIELDS,
    ExportField,
    OpticsView,
    buildRowsCsv,
    downloadCsvFile,
} from './shared'

type ExportScope = 'all' | OpticsView

const downloadCsv = (rows: DeliveryRow[], fields: ExportField[]) =>
    downloadCsvFile(buildRowsCsv(rows, fields), `fairclaim-deliveries-${new Date().toISOString().slice(0, 10)}.csv`)

interface ExportModalProps {
    initialScope: ExportScope
    includeTest: boolean
    includeHistorical?: boolean
    onClose: () => void
}

export const ExportModal = ({ initialScope, includeTest, includeHistorical = false, onClose }: ExportModalProps) => {
    const [scope, setScope] = useState<ExportScope>(initialScope)
    const [dateFrom, setDateFrom] = useState('')
    const [dateTo, setDateTo] = useState('')
    const [withTest, setWithTest] = useState(includeTest)
    const [withHistorical, setWithHistorical] = useState(includeHistorical)
    const [selected, setSelected] = useState<Record<string, boolean>>(
        Object.fromEntries(EXPORT_FIELDS.map((f) => [f.key, f.defaultOn]))
    )
    const [busy, setBusy] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [summary, setSummary] = useState<string | null>(null)

    const toggleField = (key: string) => setSelected((s) => ({ ...s, [key]: !s[key] }))
    const selectedFields = EXPORT_FIELDS.filter((f) => selected[f.key])

    const runExport = async () => {
        if (!selectedFields.length) {
            setError('Select at least one field to export')
            return
        }
        setBusy(true)
        setError(null)
        setSummary(null)
        try {
            const response = await api.post(`${BASE_URL}/api/fairclaim-deliveries-export`, {
                view: scope,
                includeTest: withTest,
                includeHistorical: withHistorical,
                dateFrom: dateFrom || undefined,
                dateTo: dateTo || undefined,
            })
            if (!response.data?.success) throw new Error(response.data?.message || 'Export request failed')
            const rows: DeliveryRow[] = response.data.rows
            if (!rows.length) {
                setError('No deliveries match the selected scope and date range')
                return
            }
            downloadCsv(rows, selectedFields)
            setSummary(
                `Exported ${rows.length} record${rows.length === 1 ? '' : 's'}` +
                    (response.data.truncated ? ' (capped at the 50,000-row export limit)' : '')
            )
        } catch (err: any) {
            setError(err?.response?.data?.message || err?.message || 'Export failed')
        } finally {
            setBusy(false)
        }
    }

    return (
        <>
            <div className={styles.slideOverBackdrop} onClick={onClose} />
            <div className={styles.exportModal}>
                <div className={styles.slideOverHeader}>
                    <h4>Export deliveries as CSV</h4>
                    <button className={styles.closeButton} onClick={onClose}>
                        Close
                    </button>
                </div>

                <div className={styles.exportControls}>
                    <label>
                        <span>Scope</span>
                        <select value={scope} onChange={(e) => setScope(e.target.value as ExportScope)}>
                            <option value="all">All deliveries</option>
                            <option value="accepted">Accepted only</option>
                            <option value="rejected">Rejected only</option>
                        </select>
                    </label>
                    <label>
                        <span>From</span>
                        <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
                    </label>
                    <label>
                        <span>To</span>
                        <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
                    </label>
                    <label className={styles.exportTestToggle}>
                        <input type="checkbox" checked={withTest} onChange={(e) => setWithTest(e.target.checked)} />
                        <span>Include test deliveries</span>
                    </label>
                    <label className={styles.exportTestToggle}>
                        <input
                            type="checkbox"
                            checked={withHistorical}
                            onChange={(e) => setWithHistorical(e.target.checked)}
                        />
                        <span>Include pre-reset testing history</span>
                    </label>
                </div>

                <h5 className={styles.sectionHeading}>Fields to include</h5>
                <div className={styles.exportFieldGrid}>
                    {EXPORT_FIELDS.map((field) => (
                        <label key={field.key}>
                            <input
                                type="checkbox"
                                checked={!!selected[field.key]}
                                onChange={() => toggleField(field.key)}
                            />
                            {field.label}
                        </label>
                    ))}
                </div>

                {error && <p className={styles.errorText}>{error}</p>}
                {summary && <p className={styles.exportSummary}>{summary}</p>}

                <div className={styles.exportFooter}>
                    <span className={styles.footnote}>
                        Leave the dates empty to export everything since the reporting reset; tick the history box to
                        also include the pre-reset testing phase. Rejection reasons are included in full so problems
                        can be reported back to the delivering party in bulk.
                    </span>
                    <button className={styles.exportButton} onClick={runExport} disabled={busy}>
                        {busy ? 'Exporting…' : 'Download CSV'}
                    </button>
                </div>
            </div>
        </>
    )
}
